import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api"

export interface CustomDescriptionUser {
    id: number
    first_name: string
    last_name: string
}

export interface CustomDescription {
    id: number
    text: string
    bill: string
    data_type: number
    organization: string
    last_user: CustomDescriptionUser
    last_text_updated_user: CustomDescriptionUser | null
    text_last_updated: string | null
    created: string
    updated: string
}

export interface CustomDescriptionResponse {
    objects: CustomDescription[]
}

const BILL_DATA_TYPE = DjangIO.app.models.QuorumDataType.bill.value

// Query keys
export const customDescriptionKeys = {
    all: ["customDescription"] as const,
    byBill: (billId: number) => [...customDescriptionKeys.all, billId] as const,
}

// Fetch custom description for a bill
const fetchCustomDescription = async (billId: number): Promise<CustomDescription | null> => {
    const response = await api.get(
        `/api/datadescription/?bill=${billId}&order_by=-updated&limit=1&full_dehydrate_last_text_updated_user=true`,
    )
    const data: CustomDescriptionResponse = await response.json()

    if (data.objects && data.objects.length > 0) {
        return data.objects[0]
    }

    return null
}

// Create new custom description
const createCustomDescription = async ({
    billId,
    text,
}: {
    billId: number
    text: string
}): Promise<CustomDescription> => {
    const billUri = `/api/bill/${billId}/`
    const organizationUri = `/api/organization/${window.Userdata.organization_id}/`
    const userUri = DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(window.Userdata.id)

    const payload = {
        bill: billUri,
        data_type: BILL_DATA_TYPE,
        text,
        organization: organizationUri,
        last_text_updated_user: userUri,
        text_last_updated: new Date().toISOString(),
    }

    const response = await api.post("/api/datadescription/", payload)
    return await response.json()
}

// Update existing custom description
const updateCustomDescription = async ({
    descriptionId,
    billId,
    text,
}: {
    descriptionId: number
    billId: number
    text: string
}): Promise<CustomDescription> => {
    const userUri = DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(window.Userdata.id)

    const payload = {
        text,
        last_text_updated_user: userUri,
        text_last_updated: new Date().toISOString(),
    }

    const response = await api.patch(`/api/datadescription/${descriptionId}/`, payload)
    return await response.json()
}

// Hook to fetch custom description
export const useCustomDescriptionQuery = (billId: number, { enabled }: { enabled?: boolean } = {}) => {
    return useQuery({
        queryKey: customDescriptionKeys.byBill(billId),
        queryFn: () => fetchCustomDescription(billId),
        enabled,
    })
}

// Hook to create custom description
export const useCreateCustomDescription = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: createCustomDescription,
        onSuccess: (_data, variables) => {
            // Invalidate the query for this bill using the billId from the mutation variables
            queryClient.invalidateQueries({
                queryKey: customDescriptionKeys.byBill(variables.billId),
            })
        },
    })
}

// Hook to update custom description
export const useUpdateCustomDescription = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: updateCustomDescription,
        onSuccess: (_data, variables) => {
            // Invalidate the query for this bill using the billId from the mutation variables
            queryClient.invalidateQueries({
                queryKey: customDescriptionKeys.byBill(variables.billId),
            })
        },
    })
}
